import { Link } from "react-router-dom";
import { ArrowRight, User, Building2, Layers, PenLine, Send, ShieldCheck, Smartphone } from "lucide-react";
import { useSettings } from "@/context/AppContext";
import { useSeo } from "@/lib/draft";
import { ShareBlock } from "@/components/ShareBlock";

export const Steps = () => (
  <ol className="grid gap-4 md:grid-cols-3" data-testid="how-it-works-steps">
    {[
      [Layers, "Selecione a área jurídica", "Escolha entre as áreas disponíveis ou indique que não sabe identificar."],
      [PenLine, "Descreva sua situação", "Conte com suas palavras, sem termos técnicos. Informe a urgência."],
      [Send, "Envie e acompanhe", "Cadastre-se só no envio, receba um protocolo e acompanhe o status."],
    ].map(([Icon, t, d], i) => (
      <li key={t} className={`card rise d${i + 1} relative overflow-hidden`}>
        <span className="font-display absolute -right-2 -top-6 text-[96px] font-extrabold text-[#EDF5FF]">{i + 1}</span>
        <div className="relative">
          <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[#1465C8] text-white"><Icon size={22} /></div>
          <h3 className="text-lg font-bold">{t}</h3>
          <p className="mt-1.5 text-sm leading-relaxed text-[#64748B]">{d}</p>
        </div>
      </li>
    ))}
  </ol>
);

const PersonaCard = ({ to, icon: Icon, title, text, testid, dark }) => (
  <Link to={to} data-testid={testid}
    className={`group flex flex-col justify-between rounded-3xl p-6 transition-transform duration-200 hover:-translate-y-1 md:p-8 ${dark ? "bg-[#1465C8] text-white" : "border-2 border-[#3198EF]/25 bg-white"}`}>
    <div>
      <div className={`mb-6 flex h-12 w-12 items-center justify-center rounded-2xl ${dark ? "bg-white/15" : "bg-[#EDF5FF] text-[#1465C8]"}`}><Icon size={24} /></div>
      <h3 className="font-display text-xl font-extrabold tracking-wide">{title}</h3>
      <p className={`mt-2 text-base ${dark ? "text-[#EDF5FF]" : "text-[#64748B]"}`}>{text}</p>
    </div>
    <span className={`mt-8 inline-flex items-center gap-2 font-bold ${dark ? "" : "text-[#1465C8]"}`}>Continuar <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" /></span>
  </Link>
);

export default function Home() {
  const { settings } = useSettings() || {};
  const t = settings?.texts || {};
  useSeo(null, "Informe a área do Direito, descreva sua situação e indique o nível de urgência. Gratuito para cadastro, envio e acompanhamento.");
  return (
    <>
      <section className="relative overflow-hidden bg-[#092C66] text-white">
        <div className="grid-bg absolute inset-0 opacity-40" />
        <div className="absolute -right-32 top-10 h-[420px] w-[420px] rounded-full border-[48px] border-[#1779E6]/30" />
        <div className="wrap relative grid gap-10 pb-20 pt-14 md:pt-24 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <p className="eyebrow rise !text-[#3198EF]">Gratuito para cadastro, envio e acompanhamento</p>
            <h1 className="rise d1 mt-4 text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {t.hero_title || "Sua questão jurídica, em poucos passos."}
            </h1>
            <p className="rise d2 mt-6 max-w-xl text-base text-[#EDF5FF]/85 md:text-lg" data-testid="hero-subtitle">{t.hero_subtitle}</p>
            <div className="rise d3 mt-9 flex flex-col gap-3 sm:flex-row">
              <Link to="/solicitar" className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-xl bg-[#1779E6] px-8 text-base font-extrabold tracking-wide transition-colors hover:bg-[#3198EF]" data-testid="hero-start-request-btn">
                INICIAR SOLICITAÇÃO <ArrowRight size={20} />
              </Link>
              <Link to="/como-funciona" className="inline-flex min-h-[56px] items-center justify-center rounded-xl border border-white/25 px-6 font-semibold transition-colors hover:bg-white/10" data-testid="hero-how-it-works-link">Como funciona</Link>
            </div>
            <p className="rise d4 mt-6 flex items-center gap-2 text-sm text-[#EDF5FF]/70"><ShieldCheck size={16} /> Você só se cadastra no momento do envio.</p>
          </div>
          <div className="rise d2 hidden lg:block">
            <div className="rounded-3xl border border-white/15 bg-white/5 p-6 backdrop-blur-md">
              <p className="text-xs font-bold uppercase tracking-widest text-[#3198EF]">Prévia da solicitação</p>
              {[["Tipo", "Pessoa Física"], ["Área", "Direito do Consumidor"], ["Urgência", "Prioritária"], ["Status", "Solicitação recebida"]].map(([k, v]) => (
                <div key={k} className="mt-4 flex items-center justify-between border-b border-white/10 pb-3 text-sm"><span className="text-[#EDF5FF]/60">{k}</span><span className="font-semibold">{v}</span></div>
              ))}
              <p className="mt-5 font-mono text-sm text-[#3198EF]">Protocolo CL-0000-000000</p>
            </div>
          </div>
        </div>
      </section>

      <section className="wrap -mt-10 relative grid gap-4 md:grid-cols-2" data-testid="persona-cards">
        <PersonaCard to="/solicitar?tipo=pf" icon={User} title="PESSOA FÍSICA" text="Preciso de orientação sobre uma situação pessoal." testid="persona-pf-continue-btn" dark />
        <PersonaCard to="/empresas" icon={Building2} title="EMPRESA" text="Busco orientação jurídica para minha empresa." testid="persona-pj-continue-btn" />
      </section>

      <section className="wrap mt-20">
        <p className="eyebrow">Como funciona</p>
        <h2 className="mt-2 text-3xl font-extrabold md:text-4xl">Três passos. Sem burocracia.</h2>
        <div className="mt-8"><Steps /></div>
      </section>

      <section className="wrap mt-20 grid gap-6 rounded-3xl bg-[#EDF5FF] p-6 md:grid-cols-[1fr_auto] md:p-10">
        <div>
          <div className="flex items-center gap-2 text-[#1465C8]"><Smartphone size={20} /><span className="eyebrow">Aplicativo</span></div>
          <h2 className="mt-2 text-2xl font-extrabold md:text-3xl">Leve a Conexão Litigium na tela inicial</h2>
          <p className="mt-2 max-w-lg text-[#64748B]">Instale em segundos, sem loja de aplicativos. Funciona no Android e no iPhone.</p>
          <Link to="/instalar" className="btn-primary mt-6" data-testid="home-install-link">Ver como instalar</Link>
        </div>
        <ShareBlock path="/" compact testid="home-share" />
      </section>

      <section className="wrap mt-16">
        <p className="max-w-3xl text-xs leading-relaxed text-[#64748B]" data-testid="home-disclaimer">
          A Conexão Litigium é uma plataforma tecnológica de organização de solicitações. O envio não garante atendimento imediato, não suspende prazos legais e não
          representa aceitação de representação profissional. Não há promessa de resultado.
        </p>
      </section>
    </>
  );
}
